import type { CSSProperties, ReactNode } from 'react';
import type { ThemeTokens } from '@course-authoring/shared-types';

export type TitleLevel = 'h1' | 'h2' | 'h3' | 'h4';

const TITLE_LEVEL_OPTIONS: TitleLevel[] = ['h1', 'h2', 'h3', 'h4'];

interface TitleLevelSelectorProps {
  currentLevel: TitleLevel;
  onSelectLevel: (level: TitleLevel) => void;
  tokens: ThemeTokens;
}

export function TitleLevelSelector({
  currentLevel,
  onSelectLevel,
  tokens,
}: TitleLevelSelectorProps): ReactNode {
  const containerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.sm,
    marginBottom: tokens.spacing.xs,
  };

  const labelStyle: CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens.text.muted,
  };

  const buttonBaseStyle: CSSProperties = {
    minWidth: '40px',
    padding: '4px 8px',
    borderRadius: tokens.radius.sm,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: tokens.borders.default,
    backgroundColor: tokens.backgrounds.app,
    color: tokens.text.primary,
    fontFamily: tokens.typography.fontFamily,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  };

  const activeButtonStyle: CSSProperties = {
    backgroundColor: tokens.ui.selectionBorder,
    color: tokens.text.onInverse,
    borderColor: tokens.ui.selectionBorder,
  };

  return (
    <div style={containerStyle}>
      <span style={labelStyle}>Title style:</span>
      {TITLE_LEVEL_OPTIONS.map((level) => (
        <button
          key={level}
          type="button"
          style={{
            ...buttonBaseStyle,
            ...(currentLevel === level ? activeButtonStyle : {}),
          }}
          aria-pressed={currentLevel === level}
          onClick={() => onSelectLevel(level)}
        >
          {level.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/**
 * Returns the typography style for a given title level.
 */
export interface TitleTypography {
  fontSize: string;
  fontWeight: number;
  lineHeight: string;
}

export function getTitleTypography(
  level: TitleLevel,
  tokens: ThemeTokens
): TitleTypography {
  const typography = tokens.typography;
  switch (level) {
    case 'h1':
      return {
        fontSize: typography.h1.size,
        fontWeight: typography.h1.weight,
        lineHeight: typography.h1.lineHeight,
      };
    case 'h2':
      return {
        fontSize: typography.h2.size,
        fontWeight: typography.h2.weight,
        lineHeight: typography.h2.lineHeight,
      };
    case 'h3':
      return {
        fontSize: typography.h3.size,
        fontWeight: typography.h3.weight,
        lineHeight: typography.h3.lineHeight,
      };
    case 'h4':
      return {
        fontSize: typography.h4.size,
        fontWeight: typography.h4.weight,
        lineHeight: typography.h4.lineHeight,
      };
  }
}

/**
 * Returns CSSProperties for a title `<input>` that auto-sizes to the heading level.
 * Use this to ensure inputs expand to fit large heading fonts (H1 = 60px, etc.).
 */
export function getTitleInputStyle(
  level: TitleLevel,
  tokens: ThemeTokens
): CSSProperties {
  const typo = getTitleTypography(level, tokens);
  return {
    width: '100%',
    height: 'auto',
    boxSizing: 'border-box',
    border: 'none',
    borderBottom: `1px solid ${tokens.borders.default}`,
    outline: 'none',
    backgroundColor: 'transparent',
    color: tokens.text.primary,
    fontFamily: tokens.typography.fontFamily,
    fontSize: typo.fontSize,
    fontWeight: typo.fontWeight,
    lineHeight: typo.lineHeight,
    padding: `${tokens.spacing.md} 0`,
  };
}
